/**
 * Printing a report, to paper or to a PDF (issue #52, the operator on
 * 2026-10-09: "There should be an option to each report either to printer or
 * PDF").
 *
 * ONE BUTTON, BECAUSE THE BROWSER ALREADY HAS BOTH. The print dialog of every
 * browser this application supports offers a printer and "Save as PDF" as
 * destinations, so a second button that built a PDF on the server would be a
 * second copy of the same page, laid out by different code, that could
 * disagree with the screen. The label names both, so nobody has to know the
 * dialog does.
 *
 * WHAT PRINTS is the report and the sentences around it: `@media print` in
 * styles.css drops the app's bar, the tabs, the period controls and every
 * button, and switches to the light palette. What it ADDS is this heading,
 * which exists only on paper: the business the figures belong to, what the
 * report covers when the screen says it in a control rather than a sentence,
 * and when it was printed -- a page that leaves the screen has lost the URL
 * and the date that told you what it was.
 *
 * The business is named as the IRS knows it (Settings' Business Details):
 * the legal name, or the trade name. A business with neither entered prints
 * without one rather than with a guess.
 */

import { useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import { api } from '../api.js';
import { formatTimestamp } from '../lib/timezone.js';

export function PrintButton() {
  return (
    <button type="button" className="no-print" onClick={() => window.print()}>
      Print Or Save As PDF
    </button>
  );
}

export function PrintHeading({ covers }: { covers?: string }) {
  const [business, setBusiness] = useState<string | null>(null);
  /*
   * WHEN IT WAS PRINTED, read as the browser prepares to print (review PR-1).
   * Read once at render, a report opened in the morning and printed in the
   * afternoon said the morning, and one left open overnight printed yesterday's
   * date on the page whose job is saying when it was true. `flushSync`, because
   * the browser lays the page out for printing straight after the event, and a
   * batched update would arrive after the snapshot.
   */
  const [printedAt, setPrintedAt] = useState(() => new Date().toISOString());

  useEffect(() => {
    const stamp = () => flushSync(() => setPrintedAt(new Date().toISOString()));
    window.addEventListener('beforeprint', stamp);
    return () => window.removeEventListener('beforeprint', stamp);
  }, []);

  useEffect(() => {
    let live = true;
    api
      .getBusinessDetails()
      .then((result) => {
        if (live) setBusiness(result.details.legalName ?? result.details.tradeName ?? null);
      })
      // A heading that cannot name the business still dates the page.
      .catch(() => {});
    return () => {
      live = false;
    };
  }, []);

  return (
    <div className="print-only print-heading">
      {business && <p className="print-business">{business}</p>}
      {covers && <p>{covers}</p>}
      <p>Printed {formatTimestamp(printedAt)}</p>
    </div>
  );
}
